/** @forge-header
 * @id          polyPropsEvents
 * @role        Polymarket MLB props/events slug classifier + player-name slugify | cross-venue join keys
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/sports/mlb:MlbPropStat
 * @downstream  kalshiTicker/polymarket | stores/marketStore (getMlbPlayerProps/getMlbEventMarkets join)
 * @exports     slugifyPlayerName:fn | titleCasePolySlug:fn | classifyPolyMlbExtendedSuffix:fn | classifyPolyExtendedTicker:fn | POLY_MLB_PROP_STAT
 * @test        polyPropsEvents.test.ts
 */

// ── Polymarket MLB player props + per-game event markets (poly-props-events,
// 2026-07-16) ────────────────────────────────────────────────────────────
//
// The Poly game event `mlb-{away}-{home}-YYYY-MM-DD` additionally carries two
// per-game binaries, and a separate `{game}-player-props` event carries the
// prop markets. On the wire they arrive as hydra-synthesized PM tickers riding
// the SAME game stem the game lines use:
//
//   PM-mlb-tb-bos-2026-07-17-nrfi                  (binary; **Poly YES = a run
//     IS scored in the 1st inning** — YRFI polarity despite the slug name,
//     live-verified vs Gamma 2026-07-16. SAME polarity as Kalshi KXMLBRFI YES:
//     the join must NOT flip — flipping renders inverted prices.)
//   PM-mlb-tb-bos-2026-07-17-extra-innings         (binary; YES = extras)
//   PM-mlb-nym-phi-2026-07-16-hr-aj-ewing-0pt5     (A.J. Ewing 1+ HR)
//   PM-mlb-det-cle-2026-07-16-k-casey-mize-3pt5    (Casey Mize 4+ Ks)
//
// Prop grammar: `-{stat}-{player-slug}-{XptY}` where stat ∈ {hr, k} (a strict
// subset of the 8 Kalshi stats — team totals and HIT/RBI/TB/SB/OUTS/HRR have
// no Poly side, ever), `XptY` = X.Y (`0pt5` = 0.5 = the "1+" line; Kalshi
// floor_strike ≡ the XptY value, so Kalshi threshold N joins line N − 0.5).
// Player slug: lowercase, hyphenated, diacritics stripped, periods removed
// then joined (`aj-ewing`, `gabriel-rincones-jr`).
//
// These are classified here (by ticker, like the Kalshi side's
// classifyMlbExtendedTicker) because the wire marketType is not authoritative;
// marketStore's parsePolyTicker DROPS them from the game-lines selectors and
// getMlbPlayerProps / getMlbEventMarkets classify them directly.

import type { MlbPropStat } from './sports/mlb';

/** Poly stat token → the Kalshi-side stat family it joins. */
export const POLY_MLB_PROP_STAT: Record<string, MlbPropStat> = {
  hr: 'hr', // ↔ KXMLBHR
  k: 'ks', // ↔ KXMLBKS
};

export type PolyMlbExtendedClass =
  | {
      kind: 'prop';
      stat: MlbPropStat;
      /** Poly's player slug segment — the join key vs slugifyPlayerName(kalshi name). */
      playerSlug: string;
      /** The X.5 line from the XptY token (≡ Kalshi floor_strike). */
      line: number;
      /** Kalshi yes-ladder threshold this line joins: line + 0.5 (0.5 → "1+"). */
      threshold: number;
    }
  /** `-nrfi` — SAME polarity as Kalshi KXMLBRFI (YES = run scores). No flip. */
  | { kind: 'rfi' }
  | { kind: 'extras' };

/**
 * Slugify a player name the way Polymarket builds its prop slug segments:
 * lowercase, strip diacritics, remove periods THEN join, spaces→hyphens.
 *   "A.J. Ewing"        → "aj-ewing"
 *   "Fernando Tatis Jr."→ "fernando-tatis-jr"
 *   "Germán Márquez"    → "german-marquez"
 * Any character outside Poly's slug alphabet ([a-z0-9-]) that survives the
 * contracted steps (apostrophes etc.) is dropped — Gamma slugs cannot carry
 * it, so removal is the only representation that can ever match; if Poly
 * instead hyphenates such a name the keys just miss and the row renders
 * unpaired (fail-safe, per the never-fuzzy-match rule).
 */
export function slugifyPlayerName(name: string): string {
  return name
    .normalize('NFD')
    .replace(/\p{M}/gu, '') // strip combining marks post-NFD (Germán → German)
    .replace(/\./g, '') // remove periods THEN join: "A.J." → "AJ" → "aj"
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .join('-');
}

/** Display fallback for a Poly-only ladder ("aj-ewing" → "Aj Ewing") — the
 *  slug is all we have when no Kalshi name joins; honest title-case, no
 *  guessed capitalization. */
export function titleCasePolySlug(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((seg) => seg[0].toUpperCase() + seg.slice(1))
    .join(' ');
}

/**
 * Classify the suffix past the PM game stem (`-nrfi`, `-extra-innings`,
 * `-{hr|k}-{player-slug}-{XptY}`). Null for every other suffix — game lines
 * keep flowing through parsePolyTicker, and unrecognized shapes (e.g. the
 * uncontracted `-first-five-winner-*` or `-team-total-*`, which the engines
 * don't ingest) are never guessed at.
 */
export function classifyPolyMlbExtendedSuffix(suffix: string): PolyMlbExtendedClass | null {
  if (suffix === '-nrfi') return { kind: 'rfi' };
  if (suffix === '-extra-innings') return { kind: 'extras' };
  // `-{hr|k}-{player-slug}-{XptY}` — the line token is always the LAST
  // segment, so the greedy slug group backtracks off it.
  const prop = suffix.match(/^-(hr|k)-([a-z0-9]+(?:-[a-z0-9]+)*)-(\d+)pt(\d+)$/);
  if (prop) {
    const line = Number(`${prop[3]}.${prop[4]}`);
    if (!Number.isFinite(line)) return null;
    return {
      kind: 'prop',
      stat: POLY_MLB_PROP_STAT[prop[1]],
      playerSlug: prop[2],
      line,
      threshold: line + 0.5,
    };
  }
  return null;
}

/**
 * Full-ticker classifier: match the hydra-synthesized PM game stem and
 * dispatch the suffix per sport. MLB only for now — the parser is structured
 * per-sport so the soccer/NFL slices are additive: no per-match World Cup
 * props surfaced on Gamma as of 2026-07-16 (the final ~Jul 19 is the next
 * chance to verify) and NFL is offseason, so their token grammars are
 * unverified. Add a branch here ONLY once live-verified — never guess token
 * vocabularies (cross-repo contract).
 */
export function classifyPolyExtendedTicker(ticker: string): PolyMlbExtendedClass | null {
  const m = ticker.match(/^PM-([a-z]+)-[a-z0-9]+-[a-z0-9]+-\d{4}-\d{2}-\d{2}(-.+)$/);
  if (!m) return null;
  if (m[1] === 'mlb') return classifyPolyMlbExtendedSuffix(m[2]);
  return null;
}
